import {
  Component,
  OnDestroy,
  OnInit
}                       from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UIRouter }     from '@uirouter/angular';
import { Subscription } from 'rxjs';

import {
  CustomerLogs,
  CustomerThread,
  MessageLog
}                                  from './typings/customer-messages.customer-thread-params';
import { CustomerMessagesService } from './customer-messages.service';
import { CableSubscriptionModel }  from '../../api/websockets/typings/websocket.cable-subscription';
import { MessagesService }         from '../messages.service';
import { MessengerService }        from '../messenger/messenger.service';
import { MessengerSmsParamsModel } from '../messenger/typings/messenger.sms-params';
import { SessionService }          from '../../sessions/session.service';
import { WebSocketService }        from '../../api/websockets/websocket.service';

import messageNotificationIcon from 'icons/message-notification.svg';

@Component({
  selector   : 'wor-customer-messages-notifications',
  styles: [ require('./customer-messages.notifications.component.scss') ],
  template: require('./customer-messages.notifications.component.html')
})
export class CustomerMessagesNotificationsComponent implements OnInit, OnDestroy {
  message_logs        : CustomerThread[]      = [];
  notifications?      : CableSubscriptionModel;
  radiateTimer        : any                   = null;
  refreshCustomersSub : any;
  selectedThread      : CustomerThread | null = null;


  customerListParams      = {
    page_size: 10,
    offset   : 0
  };

  canLoadMoreCustomers    = false;
  dropdownOpen            = false;
  loadingMoreCustomers    = false;
  messageNotificationIcon = this.sanitizer.bypassSecurityTrustHtml(messageNotificationIcon);
  radiate                 = false;
  sub                     = new Subscription();
  session                 = this.sessionService.base();
  unreadCustomerCount     = 0;

  threadFetchParams       = {
    page_size: 20,
    offset   : 0
  };

  private readonly notifiableCategories = new Set<string>([
    'direct'
  ]);

  constructor(
    private customerMessagesService : CustomerMessagesService,
    private messagesService         : MessagesService,
    private messengerService        : MessengerService,
    private router                  : UIRouter,
    private sanitizer               : DomSanitizer,
    private sessionService          : SessionService,
    private webSocketService        : WebSocketService,
  ) {}

  async applySmsNotification (
    messageLogs         : CustomerThread[],
    incomingMsg         : MessageLog,
    fetchCustomerThread : (customerId: string) => Promise<CustomerThread>
    ) : Promise<CustomerThread[]> {
    const logs = Array.isArray(messageLogs) ? [...messageLogs] : [];

    const key = this.getIncomingKey(incomingMsg);
    if (!key) {
      return logs;
    }

    const idx = logs.findIndex(t => this.getThreadKey(t) === key);

    const isNotifiable = this.isNotifiableCategory(incomingMsg?.category);

    if (idx >= 0) {
      logs[idx] = this.insertMessageIntoThread({ ...logs[idx] }, incomingMsg);

      // Only notifiable should re-sort list
      return isNotifiable ? this.sortThreadsByLatest(logs) : logs;
    }

    // non-notifiable => do nothing (no fetch, no new thread), wait for refresh/reload
    if (!isNotifiable) {
      return logs;
    }

    // Known customer (notifiable): fetch full thread
    if (incomingMsg?.customer_id) {
      const newThread = await fetchCustomerThread(incomingMsg.customer_id);

      const normalized = this.insertMessageIntoThread(
        this.syncThreadMessages({ ...newThread }),
        incomingMsg
      );

      logs.push(normalized);
      return this.sortThreadsByLatest(logs);
    }

    // Unknown customer (notifiable): create a empty thread
    const baseThread : CustomerThread = {
      customer_id : null as any,
      first_name  : null as any,
      last_name   : null as any,
      company_name: null as any,
      mobile      : key,
      message_logs: []
    };

    const normalized = this.insertMessageIntoThread(
      this.syncThreadMessages({ ...baseThread }),
      incomingMsg
    );

    logs.push(normalized);
    return this.sortThreadsByLatest(logs);
  }

  closeDropdown () : void {
    this.dropdownOpen = false;
  }

  decUnreadCustomerCount () : void {
    this.unreadCustomerCount = Math.max(0, (this.unreadCustomerCount || 0) - 1);
  }

  get selectedJobCardText () : string | null {
    if (!this.selectedThread){
      return null;
    }

    const jobCard = (this.selectedThread as any).job_card_number;
    return jobCard ? `Job card #${jobCard}` : `Job card #123987345`;
  }

  getCustomerLatestMessages ( thread : CustomerThread ) : number {
    return this.parseDates(thread?.latest_message_time);
  }

  getIncomingKey ( msg : Partial<MessageLog> | null | undefined ) : string {
    return String((msg as any)?.customer_id || (msg as any)?.mobile || '');
  }

  getLatestNotifiableCreatedOn ( thread : CustomerThread ) : string | null {
    const msgs = Array.isArray(thread?.message_logs)
     ? thread.message_logs
     : [];
    for (const m of msgs) {
      if (this.isNotifiableCategory(m?.category)) {
        return m?.created_on || null;
      }
    }
    return null;
  }

  getThreadKey ( thread : Partial<CustomerThread> | null | undefined ) : string {
    return String(thread?.customer_id || thread?.mobile || '');
  }

  handleBackFromThread () : void {
    this.selectedThread = null;
  }

  handleListMenu ( ev : { action : 'view_customer' | 'create_customer' | 'open_latest_invoice' | 'mark_unread'; thread : CustomerThread }) : void {
    const { action, thread } = ev;

    switch (action) {
      case 'view_customer':
        if (thread.customer_id) {
          this.router.stateService.go('app.customer', { id: thread.customer_id });
        }
        break;

      case 'create_customer':
        this.router.stateService.go('app.customer', {
          mobile: thread?.mobile
        }, {
          inherit: false
        });

        break;

      // yet to figure out
      case 'open_latest_invoice':
        break;

      case 'mark_unread':
        this.markCustomerUnread(thread);
        break;
    }
  }

  handleLoadMoreCustomers () : void {
    if (this.loadingMoreCustomers || !this.canLoadMoreCustomers){
      return;
    }

    this.loadingMoreCustomers = true;

    this.customerMessagesService.getLatestMessagesLogs(this.customerListParams)
    .then(( res : CustomerLogs ) => {
      const nextThreads = res?.customer_logs || [];

      // like initial load
      const normalized = nextThreads.map(t => this.syncThreadMessages({ ...t }));

      // append by ( customer_id || mobile )
      const threadsByKey = new Map<string, CustomerThread>(
        (this.message_logs || [])
        .map(thread => [ this.getThreadKey(thread), thread ] as [string, CustomerThread])
        .filter(([key]) => !!key)
      );

      for (const thread of normalized) {
        const key = this.getThreadKey(thread);
        if (key && !threadsByKey.has(key)) {
          threadsByKey.set(key, thread);
        }
      }

      this.message_logs              = this.sortThreadsByLatest(Array.from(threadsByKey.values()));
      this.customerListParams.offset = res?.next_offset ?? this.customerListParams.offset;
      this.canLoadMoreCustomers      = (res?.is_last === false) && (res?.next_offset !== null);
    })
    .catch(err => this.messagesService.error(err))
    .finally(() => this.loadingMoreCustomers = false);
  }

  handleLoadMoreForSelectedThread () : void {
    const thread = this.selectedThread;
    if (!thread){
      return;
    }

    if (thread.is_last === true){
      return;
    }

    const params = {
      page_size: thread.page_size || 20,
      offset   : thread.next_offset || 0
    };

    const identifier = thread.customer_id
      ? { customer_id: thread.customer_id }
      : { mobile: thread.mobile };

    this.customerMessagesService.getCustomerSmsLogs(identifier, params)
    .then(( result : CustomerThread ) => {
      const older   = result?.message_logs || [];
      const current = thread.message_logs || [];

      // older must go AFTER current to keep DESC (latest first)
      const seen   = new Set<number | string>();
      const merged = [...current, ...older]
        .filter(message => {
          const id = (message as any).id;
          if (id == null) {
            return true;
          }
          if (seen.has(id)) {
            return false;
          }
          seen.add(id);
          return true;
        });

      thread.message_logs = merged;
      thread.next_offset  = result?.next_offset;
      thread.page_size    = result?.page_size ?? thread.page_size;
      thread.is_last      = result?.is_last;

      // recalculate ( unread/latest_time )
      this.syncThreadMessages(thread);

      // update selectedThread reference
      this.selectedThread = { ...thread, message_logs: [...thread.message_logs] };

      // update the thread inside message_logs array
      const idx = this.message_logs.findIndex(x => x.customer_id === thread.customer_id);
      if (idx >= 0) {
        const copy = [...this.message_logs];
        copy[idx]  = this.selectedThread;
        this.message_logs = copy;
      }
    })
    .catch(err => this.messagesService.error(err));
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleNewMessage () : void {}

  handleNewCustomerSelected ( customer : any ) : void {
    const customerId = customer?.customer_id || customer?.id;
    if (!customerId) {
      return;
    }

    const baseThread : CustomerThread = {
      customer_id : customerId,
      first_name  : customer?.first_name,
      last_name   : customer?.last_name,
      company_name: customer?.company_name ?? null,
      mobile      : customer?.mobile ?? null,
      message_logs: []
    };

    // customer already exists - open that thread
    const existing = (this.message_logs || []).find(t => t.customer_id === customerId);
    if (existing) {
      this.selectedThread = existing;
      this.markCustomerRead(existing);
      return;
    }

    // new customer fetch thread
    this.customerMessagesService.getCustomerSmsLogs({ customer_id: customerId }, { page_size: 20, offset: 0 })
    .then(( res: any ) => {
      const newThread =
        res && res.customer_id
        ? (res as CustomerThread)
        : baseThread;

      // Normalize + sort
      const normalized = this.syncThreadMessages({ ...newThread, message_logs: newThread.message_logs || [] });

      // Add to list + sorted
      this.message_logs = this.sortThreadsByLatest([normalized, ...(this.message_logs || [])]);

      // Open message-thread view
      this.selectedThread = normalized;
      this.markCustomerRead(normalized);
    })
    .catch(err => {
      this.selectedThread = baseThread;
      this.messagesService.error(err);
    });
  }

  handleSelectThread ( thread : CustomerThread ) : void {
    //switch to thread view
    this.selectedThread = thread;
    this.markCustomerRead(thread);
  }

  handleSendFromThread ( text: string ) : void {
    if (!this.selectedThread) {
      return;
    }

    const thread = this.selectedThread;

    const sms: MessengerSmsParamsModel = {
      customer_id: thread.customer_id,
      name       : thread.display_name,
      send_to    : thread.mobile,
      message    : text
    };

    this.messengerService.sendSms(sms)
    .catch(err => this.messagesService.error(err));
  }

  handleThreadMenu ( action : 'view_customer' | 'create_customer' | 'open_latest_invoice' | 'mark_unread' ) : void {
    if (!this.selectedThread){
      return;
    }

    switch (action) {
      case 'view_customer':
        if ( this.selectedThread.customer_id ) {
          this.router.stateService.go('app.customer', {
            id: this.selectedThread.customer_id
          });
        }
        break;

      case 'create_customer':
        this.router.stateService.go('app.customer', {
          mobile: this.selectedThread?.mobile,
        }, {
          inherit: false
        });

        break;

      // yet to figure out
      case 'open_latest_invoice':
        break;

      case 'mark_unread':
        this.markCustomerUnread(this.selectedThread);
        this.selectedThread = null;
        break;
    }
  }

  isNotifiableCategory ( category : string | null | undefined ) : boolean {
    const c = String(category || '').trim().toLowerCase();
    return this.notifiableCategories.has(c);
  }

  incUnreadCustomerCount () : void {
    this.unreadCustomerCount = (this.unreadCustomerCount || 0) + 1;
  }

  insertMessageIntoThread ( thread : CustomerThread, msg : MessageLog ) : CustomerThread {
    const msgs   = Array.isArray(thread.message_logs)? [...thread.message_logs] : [];
    const exists = msgs.some(m => m?.id === msg.id);

    const isNotifiable = this.isNotifiableCategory(msg?.category);

    // non-notifiable messages must be read by default
    const incoming : MessageLog = isNotifiable ? msg : { ...msg, unread: false };

    if ( !exists ) {
      const msgMs = this.parseDates(msg.created_on);

      // DESC order by created_on
      let inserted = false;
      for ( let i = 0; i < msgs.length; i++ ) {
        const curMs = this.parseDates(msgs[i]?.created_on);
        if ( msgMs >= curMs ) {
          msgs.splice(i, 0, incoming);
          inserted = true;
          break;
        }
      }
      if ( !inserted ) {
        msgs.push(incoming);
      }
    }

    thread.message_logs = msgs;
    // Only notifiable updates unread + latest_message_time
    return isNotifiable ? this.syncThreadMessages(thread) : thread;
  }

  loadInitialLogs () : void {
    this.customerMessagesService.getLatestMessagesLogs(this.customerListParams)
    .then(( customer_logs : CustomerLogs ) => {
      const threads                  = customer_logs?.customer_logs || [];
      this.message_logs              = this.loadInitialThreads(threads);
      this.customerListParams.offset = customer_logs?.next_offset ?? this.customerListParams.offset;
      this.canLoadMoreCustomers      = (customer_logs?.is_last === false) && (customer_logs?.next_offset !== null);
    })
    .catch(( err : any ) => {
      this.messagesService.error(err);
    });
  }

  loadInitialThreads ( messageLogsFromApi : CustomerThread[] ) : CustomerThread[] {
    const logs = (messageLogsFromApi || []).map(t => this.syncThreadMessages({ ...t }));
    return this.sortThreadsByLatest(logs);
  }

  markCustomerRead ( thread : any ,evadeDecrement? : boolean ) : void {
    if (thread) {
      const wasUnread = !!thread?.unread;
      thread.unread   = false;
      (thread.message_logs || []).forEach(( message : any) => (message.unread = false));

      if (wasUnread && !evadeDecrement) {
        this.decUnreadCustomerCount();
      }
    }

    this.notifications?.perform( 'mark_as_read',
      thread?.customer_id
      ? { customer_id: thread?.customer_id }
      : { mobile: thread?.mobile }
    );
  }

  markCustomerUnread ( thread : any ) : void {
    if (thread) {
      const wasUnread = !!thread?.unread;
      thread.unread   = true;
      const msgs      = thread.message_logs || [];

      if (msgs.length) {
        msgs[0].unread = true;
      }

      if (!wasUnread) {
        this.incUnreadCustomerCount();
      }
    }

    this.notifications?.perform( 'mark_as_unread',
      thread?.customer_id
      ? { customer_id: thread?.customer_id }
      : { mobile: thread?.mobile }
    );
  }

  markMessageRead ( smsLogId : string, customerId? : string) : void {
    if (customerId) {
      const thread = (this.message_logs || []).find((t: any) => t.customer_id === customerId);
      const msg    = thread?.message_logs?.find((m: any) => m.id === smsLogId);
      if (msg) {
        msg.unread = false;
      }
      if (thread) {
        thread.unread = thread.message_logs?.some((m: any) => m.unread) ?? false;
      }
    }

    this.notifications?.perform('mark_as_read', { sms_log_id: smsLogId });
  }

  ngOnDestroy () : void {
    document.removeEventListener('click', this.onDocClick, true);
    if (this.radiateTimer) {
      clearTimeout(this.radiateTimer);
    }
    this.sub.unsubscribe();
    if (this.refreshCustomersSub) {
      this.refreshCustomersSub.unsubscribe();
    }
  }

  ngOnInit () : void {
    document.addEventListener('click', this.onDocClick, true);

    this.webSocketService.connect();

    this.notifications = this.webSocketService.subscribe('MessagingChannel');

    setTimeout(() => {
      this.notifications?.perform('request_init');
    }, 1000);

    this.sub.add(
      this.webSocketService.notifications$().subscribe(async ({ identifier, message }) => {
        const payload = message as any;
        // INIT payload
        if (payload?.type === 'init' && typeof payload.unread_customer_count === 'number') {
          this.unreadCustomerCount = payload.unread_customer_count;
          return;
        }

        const smsLog = message?.sms_log as any;
        if (!smsLog) {
          return;
        }

        const incomingMsg = message.sms_log as MessageLog;

        if( this.isNotifiableCategory(incomingMsg?.category) ) {
          this.triggerRadiate();
        }

        const key = this.getIncomingKey(incomingMsg);
        if (!key) {
          return;
        }

        const wasUnread = !!this.message_logs.find(t => this.getThreadKey(t) === key)?.unread;

        try {
          this.message_logs = await this.applySmsNotification(
            this.message_logs,
            incomingMsg,
            (customerId) =>
            this.customerMessagesService.getCustomerSmsLogs( { customer_id: customerId }, this.threadFetchParams )
            .then((res: CustomerThread) => res)
          );

          const nowUnread = !!this.message_logs.find(t => this.getThreadKey(t) === key)?.unread;

          // Increment badge only if this message caused
          if (!wasUnread && nowUnread) {
            this.incUnreadCustomerCount();
          }

          if (this.selectedThread && this.getThreadKey(this.selectedThread) === key) {
            this.insertMessageIntoThread(this.selectedThread, incomingMsg);
            this.selectedThread = { ...this.selectedThread, message_logs: [...this.selectedThread.message_logs] };
            if ( this.isNotifiableCategory(incomingMsg?.category) && this.dropdownOpen ) {
              this.markCustomerRead(this.selectedThread);
              this.markCustomerRead(this.message_logs.find(t => this.getThreadKey(t) === key), true);
            }
          }
        } catch (err) {
           this.messagesService.error(err);
        }
      })
    );

    this.loadInitialLogs();

    this.refreshCustomersSub = this.customerMessagesService.refreshCustomerSmsLogs$
    .subscribe(() => this.refreshSmsLogs());
  }

  onDocClick = ( evt : MouseEvent ) => {
    if ( !this.dropdownOpen ) {
      return;
    }
    const target = evt.target as HTMLElement;
    if (!target.closest('.notif-host')) {
      this.dropdownOpen = false;
    }
  };

  parseDates ( dateTime : string | null | undefined ) : number {
    if (!dateTime) {
      return 0;
    }
    const dt = Date.parse(dateTime);
    return Number.isFinite(dt) ? dt : 0;
  }

  private refreshSmsLogs () : void {
    this.customerListParams.page_size = 10;
    this.customerListParams.offset    = 0;
    this.loadInitialLogs();
  }

  triggerRadiate ( ms = 4000 ) : void {
    this.radiate = true;

    if (this.radiateTimer) {
      clearTimeout(this.radiateTimer);
    }

    this.radiateTimer = setTimeout(() => {
      this.radiate      = false;
      this.radiateTimer = null;
    }, ms);
  }

  sortThreadsByLatest ( messageLogs : CustomerThread[] ) : CustomerThread[] {
    return [...(messageLogs || [])].sort((a, b) => this.getCustomerLatestMessages(b) - this.getCustomerLatestMessages(a));
  }

  syncThreadMessages ( thread : CustomerThread ) : CustomerThread {
    const msgs    = Array.isArray(thread.message_logs) ? thread.message_logs : [];

    // Only notifiable messages affect unread + Customer list "latest"
    thread.unread = msgs.some(m => this.isNotifiableCategory(m?.category) && m?.unread === true);

    const latestNotifiable = this.getLatestNotifiableCreatedOn(thread);

    thread.latest_message_time = latestNotifiable
      || thread.latest_message_time
      || msgs[0]?.created_on
      || null as any;

    return thread;
  }

  toggleDropdown ( ev? : Event ) : void {
    if (ev) {
      ev.stopPropagation();
    }
    this.dropdownOpen = !this.dropdownOpen;
    if(this.selectedThread?.unread){
      this.markCustomerRead(this.selectedThread);
      const key = this.getThreadKey(this.selectedThread);
      this.markCustomerRead(this.message_logs.find(t => this.getThreadKey(t) === key), true);
    }
  }

}
